import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Send, CheckCircle, ChevronRight, ChevronLeft,
  Smile, Meh, Frown, AlertCircle, Sparkles, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mood options
const MOODS = [
  { id: 'great', icon: Smile, label: 'Great! 🌟', color: 'bg-green-500 hover:bg-green-600', textColor: 'text-green-600' },
  { id: 'good', icon: Smile, label: 'Good 😊', color: 'bg-lime-500 hover:bg-lime-600', textColor: 'text-lime-600' },
  { id: 'okay', icon: Meh, label: 'Okay 😐', color: 'bg-yellow-500 hover:bg-yellow-600', textColor: 'text-yellow-600' },
  { id: 'struggling', icon: Frown, label: 'Struggling 😔', color: 'bg-orange-500 hover:bg-orange-600', textColor: 'text-orange-600' },
  { id: 'tough', icon: AlertCircle, label: 'Tough Week 😢', color: 'bg-red-500 hover:bg-red-600', textColor: 'text-red-600' }
];

// Survey questions - based on youth employment program best practices
const SURVEY_QUESTIONS = [
  {
    id: 'job_satisfaction',
    question: 'How satisfied are you with your work experience this week?',
    type: 'rating',
    options: ['Very Unsatisfied', 'Unsatisfied', 'Neutral', 'Satisfied', 'Very Satisfied']
  },
  {
    id: 'skill_learning',
    question: 'Did you learn any new skills at work this week?',
    type: 'rating',
    options: ['Not at all', 'A little', 'Somewhat', 'A lot', 'Definitely!']
  },
  {
    id: 'supervisor_support',
    question: 'How supported do you feel by your supervisor/manager?',
    type: 'rating',
    options: ['Not Supported', 'Slightly', 'Somewhat', 'Well Supported', 'Very Supported']
  },
  {
    id: 'personal_wellbeing',
    question: 'How are you feeling personally outside of work?',
    type: 'rating',
    options: ['Struggling', 'A bit down', 'Okay', 'Pretty good', 'Great!']
  },
  {
    id: 'goal_progress',
    question: 'How much progress did you make on your goals this week?',
    type: 'rating',
    options: ['No progress', 'A little', 'Some progress', 'Good progress', 'Excellent!']
  },
  {
    id: 'confidence_growth',
    question: 'Do you feel more confident in your abilities compared to last week?',
    type: 'rating',
    options: ['Less confident', 'About the same', 'A bit more', 'More confident', 'Much more!']
  },
  {
    id: 'program_rating',
    question: 'Overall, how would you rate this week in SYEP?',
    type: 'rating',
    options: ['⭐', '⭐⭐', '⭐⭐⭐', '⭐⭐⭐⭐', '⭐⭐⭐⭐⭐']
  }
];

const MandatoryCheckinModal = ({ user, onComplete }) => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1); // 1: Mood, 2: Open questions, 3: Survey, 4: Confirmation
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [weekNumber, setWeekNumber] = useState(0);
  const [deadline, setDeadline] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    mood: '',
    highlight: '',
    challenge: '',
    learning: '',
    suggestion: '',
    survey_responses: {}
  });

  useEffect(() => {
    checkCheckinStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkCheckinStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/checkins/mandatory-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setWeekNumber(data.week_number);
        setDeadline(data.deadline);
        
        // Show modal if check-in is required and within the window
        if (data.is_required && !data.is_completed) {
          setShow(true);
        }
      }
    } catch (error) {
      console.error('Error checking check-in status:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    // Validate all required fields
    if (!formData.mood) {
      toast.error('Please select your mood for the week');
      setStep(1);
      return;
    }
    if (!formData.highlight.trim()) {
      toast.error('Please share a highlight from your week');
      setStep(2);
      return;
    }
    
    // Check all survey questions answered
    const unanswered = SURVEY_QUESTIONS.filter(q => !formData.survey_responses[q.id]);
    if (unanswered.length > 0) {
      toast.error('Please answer all survey questions');
      setStep(3);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/checkins/submit-weekly`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          week_number: weekNumber,
          mood: formData.mood,
          highlight: formData.highlight,
          challenge: formData.challenge,
          learning: formData.learning,
          suggestion: formData.suggestion,
          survey_responses: formData.survey_responses
        })
      });

      if (res.ok) {
        const data = await res.json();
        
        // Celebration!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        toast.success(`🎉 Week ${weekNumber} check-in complete! +${data.points_earned} points`);
        setStep(4);
        
        setTimeout(() => {
          setShow(false);
          if (onComplete) onComplete();
        }, 2000);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit check-in');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }
    setSubmitting(false);
  };

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  if (loading || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold">Week {weekNumber} Check-in</h2>
                <p className="text-sm opacity-90">Tell us how your week went!</p>
              </div>
            </div>
            
            {/* Progress */}
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1">
                <span>Step {step} of {totalSteps}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2 bg-white/30" />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {/* Step 1: Mood */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h3 className="font-heading text-xl font-bold text-center mb-6">
                  How are you feeling this week?
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {MOODS.map((mood) => {
                    const Icon = mood.icon;
                    const isSelected = formData.mood === mood.id;
                    return (
                      <button
                        key={mood.id}
                        onClick={() => setFormData({ ...formData, mood: mood.id })}
                        className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                          isSelected 
                            ? `${mood.color} text-white border-transparent` 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`mood-${mood.id}`}
                      >
                        <Icon className={`w-8 h-8 ${isSelected ? 'text-white' : mood.textColor}`} />
                        <span className="font-semibold text-lg">{mood.label}</span>
                        {isSelected && <CheckCircle className="w-6 h-6 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 2: Open Questions */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div>
                  <label className="font-semibold block mb-2">
                    What was the highlight of your week? *
                  </label>
                  <Textarea
                    placeholder="Share something positive that happened..."
                    value={formData.highlight}
                    onChange={(e) => setFormData({ ...formData, highlight: e.target.value })}
                    className="rounded-xl min-h-[80px]"
                    maxLength={500}
                    data-testid="highlight-input"
                  />
                </div>
                
                <div>
                  <label className="font-semibold block mb-2">
                    What challenges did you face? (optional)
                  </label>
                  <Textarea
                    placeholder="Any obstacles or difficulties..."
                    value={formData.challenge}
                    onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                    className="rounded-xl min-h-[80px]"
                    maxLength={500}
                    data-testid="challenge-input"
                  />
                </div>

                <div>
                  <label className="font-semibold block mb-2">
                    What did you learn this week? (optional)
                  </label>
                  <Textarea
                    placeholder="New skills, insights, or lessons..."
                    value={formData.learning}
                    onChange={(e) => setFormData({ ...formData, learning: e.target.value })}
                    className="rounded-xl min-h-[80px]"
                    maxLength={500}
                    data-testid="learning-input"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Survey */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h3 className="font-heading text-lg font-bold text-center mb-4">
                  Quick Weekly Survey
                </h3>
                {SURVEY_QUESTIONS.map((q, qIndex) => (
                  <div key={q.id} className="space-y-2">
                    <p className="font-medium text-sm">{qIndex + 1}. {q.question}</p>
                    <div className="flex gap-2 flex-wrap">
                      {q.options.map((option, oIndex) => (
                        <button
                          key={oIndex}
                          onClick={() => setFormData({
                            ...formData,
                            survey_responses: { ...formData.survey_responses, [q.id]: oIndex + 1 }
                          })}
                          className={`px-3 py-2 rounded-full text-xs font-medium transition-all ${
                            formData.survey_responses[q.id] === oIndex + 1
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          data-testid={`survey-${q.id}-${oIndex}`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Suggestion box */}
                <div className="pt-4 border-t">
                  <label className="font-semibold block mb-2">
                    Any suggestions for the program? (optional)
                  </label>
                  <Textarea
                    placeholder="How can we make SYEP better?"
                    value={formData.suggestion}
                    onChange={(e) => setFormData({ ...formData, suggestion: e.target.value })}
                    className="rounded-xl min-h-[60px]"
                    maxLength={300}
                    data-testid="suggestion-input"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-green-600 mb-2">
                  Check-in Complete!
                </h3>
                <p className="text-muted-foreground">
                  Thanks for sharing your week with us! Keep up the great work! 🌟
                </p>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          {step < 4 && (
            <div className="p-4 border-t bg-gray-50 flex justify-between gap-3">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button
                  onClick={() => {
                    if (step === 1 && !formData.mood) {
                      toast.error('Please select your mood');
                      return;
                    }
                    if (step === 2 && !formData.highlight.trim()) {
                      toast.error('Please share a highlight');
                      return;
                    }
                    setStep(step + 1);
                  }}
                  className="rounded-full bg-primary"
                  data-testid="next-btn"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-full bg-green-500 hover:bg-green-600"
                  data-testid="submit-checkin-btn"
                >
                  {submitting ? 'Submitting...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Submit Check-in
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Deadline notice */}
          {deadline && step < 4 && (
            <div className="px-4 pb-4">
              <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-2 text-amber-700 text-sm">
                <Clock className="w-4 h-4" />
                <span>Due by {new Date(deadline).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} at noon</span>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MandatoryCheckinModal;
